import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../@types/AsyncStatus';
import { AttributionRole } from '../@types/CoproUserAttribution';
import type { User } from '../@types/User';
import type { RootState } from '../store/store';
import { JWTHeader } from '../utils/JWTHeader';

interface RawAttribution {
  id?: number;
  fonctionType?: string;
  user?: User;
}

export interface CompanyAttributionUsers {
  referentSignataires: User[];
  dpeQualificationManagers: User[];
  agDocumentManagers: User[];
}

const EMPTY_RESULT: CompanyAttributionUsers = {
  referentSignataires: [],
  dpeQualificationManagers: [],
  agDocumentManagers: [],
};

const ROLE_TO_BUCKET: Record<string, keyof CompanyAttributionUsers> = {
  [AttributionRole.REFERENT]: 'referentSignataires',
  [AttributionRole.ASSISTANT_REFERENT]: 'dpeQualificationManagers',
  [AttributionRole.ASSISTANT_GESTIONNAIRE]: 'agDocumentManagers',
};

const dedupeUsers = (users: User[]): User[] => {
  const seen = new Map<number, User>();
  for (const user of users) {
    if (typeof user?.id === 'number' && !seen.has(user.id)) {
      seen.set(user.id, user);
    }
  }
  return [...seen.values()].sort((a, b) => {
    const lastA = (a.lastname || '').toLowerCase();
    const lastB = (b.lastname || '').toLowerCase();
    if (lastA !== lastB) {
      return lastA.localeCompare(lastB);
    }
    return (a.firstname || '').toLowerCase().localeCompare((b.firstname || '').toLowerCase());
  });
};

export function useCompanyAttributionUsers(companyId: number | null | undefined): {
  attributionUsers: CompanyAttributionUsers;
  status: AsyncStatus;
} {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [attributionUsers, setAttributionUsers] = useState<CompanyAttributionUsers>(EMPTY_RESULT);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');

  useEffect(() => {
    if (!companyId || !jwt) {
      setAttributionUsers(EMPTY_RESULT);
      setStatus('IDLE');
      return;
    }

    let aborted = false;
    const run = async() => {
      try {
        setStatus('PENDING');
        const response = await fetch(`/api/companies/${companyId}/validated-copros/attributions`, JWTHeader(jwt));
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = await response.json();
        const items: RawAttribution[] = Array.isArray(payload)
          ? payload
          : (payload?.results ?? []);

        const buckets: Record<keyof CompanyAttributionUsers, User[]> = {
          referentSignataires: [],
          dpeQualificationManagers: [],
          agDocumentManagers: [],
        };

        for (const attr of items) {
          if (!attr?.user || typeof attr.fonctionType !== 'string') { continue; }
          const bucketKey = ROLE_TO_BUCKET[attr.fonctionType];
          if (!bucketKey) { continue; }
          buckets[bucketKey].push(attr.user);
        }

        if (aborted) { return; }
        setAttributionUsers({
          referentSignataires: dedupeUsers(buckets.referentSignataires),
          dpeQualificationManagers: dedupeUsers(buckets.dpeQualificationManagers),
          agDocumentManagers: dedupeUsers(buckets.agDocumentManagers),
        });
        setStatus('SUCCESS');
      } catch {
        if (aborted) { return; }
        setAttributionUsers(EMPTY_RESULT);
        setStatus('ERROR');
      }
    };
    run();
    return () => { aborted = true; };
  }, [companyId, jwt]);

  return { attributionUsers, status };
}

export default useCompanyAttributionUsers;
